import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { FaArrowRight, FaBars, FaTimes } from 'react-icons/fa';
import { useLanguage } from '../i18n';

const Header = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { language, toggleLanguage, t } = useLanguage();

    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 12);
        handleScroll();
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const scrollToSection = (sectionId) => {
        if (location.pathname !== '/') {
            navigate('/', { state: { scrollTo: sectionId } });
            setIsMobileMenuOpen(false);
            return;
        }

        document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
        setIsMobileMenuOpen(false);
    };

    const navItems = [
        { name: t('home'), id: 'hero' },
        { name: t('services'), id: 'services' },
        { name: t('projects'), id: 'case-studies' },
        { name: t('pricing'), id: 'pricing' },
        { name: t('faq'), id: 'faq' }
    ];

    return (
        <>
            <header
                className={`fixed inset-x-0 top-0 z-50 border-b transition-all duration-300 ${isScrolled
                    ? 'border-gray-200 bg-paper py-3'
                    : 'border-transparent bg-paper py-5'
                    }`}
            >
                <nav className="container mx-auto flex items-center justify-between px-5 lg:px-8" aria-label="Điều hướng chính">
                    <button
                        type="button"
                        onClick={() => scrollToSection('hero')}
                        className="group flex min-h-11 items-center gap-3 text-left"
                        aria-label="Về đầu trang DevenflyWeb"
                    >
                        <img
                            src="/devenfly-wordmark.png"
                            alt="DevenflyWeb"
                            className="h-auto w-36 object-contain sm:w-44"
                        />
                    </button>

                    <div className="hidden items-center gap-7 lg:flex">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                onClick={() => scrollToSection(item.id)}
                                className="group relative min-h-11 px-1 text-sm font-semibold text-gray-600 transition-colors hover:text-ink"
                            >
                                {item.name}
                                <span className="absolute bottom-1 left-1 h-0.5 w-0 bg-primary-600 transition-all duration-200 group-hover:w-[calc(100%-0.5rem)]" />
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        <motion.button
                            type="button"
                            onClick={() => scrollToSection('contact')}
                            className="hidden min-h-11 items-center gap-2 rounded-lg bg-ink px-5 text-sm font-bold text-white transition-colors hover:bg-primary-600 md:flex"
                            whileTap={{ scale: 0.96 }}
                        >
                            {t('startProject')} <FaArrowRight className="text-xs" />
                        </motion.button>
                        <button
                            type="button"
                            onClick={toggleLanguage}
                            className="hidden min-h-11 rounded-lg border border-gray-200 px-3 text-xs font-bold tracking-wide text-ink transition-colors hover:border-primary-300 hover:text-primary-700 sm:block"
                            aria-label={`${t('languageLabel')}: ${language === 'vi' ? 'English' : 'Tiếng Việt'}`}
                        >
                            {language === 'vi' ? 'EN' : 'VI'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setIsMobileMenuOpen((open) => !open)}
                            className="flex h-11 w-11 items-center justify-center rounded-lg border border-gray-200 text-ink transition-colors hover:border-primary-300 hover:text-primary-700 lg:hidden"
                            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
                            aria-expanded={isMobileMenuOpen}
                        >
                            {isMobileMenuOpen ? <FaTimes size={18} /> : <FaBars size={18} />}
                        </button>
                    </div>
                </nav>

                <AnimatePresence>
                    {isMobileMenuOpen && (
                        <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.22 }}
                            className="border-t border-gray-200/80 bg-paper lg:hidden"
                        >
                            <div className="container mx-auto flex flex-col gap-1 px-5 py-4">
                                {navItems.map((item) => (
                                    <button
                                        key={item.id}
                                        type="button"
                                        onClick={() => scrollToSection(item.id)}
                                        className="min-h-11 rounded-lg px-3 text-left font-semibold text-gray-600 transition-colors hover:bg-primary-50 hover:text-primary-700"
                                    >
                                        {item.name}
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    onClick={toggleLanguage}
                                    className="mb-2 flex min-h-11 items-center justify-between rounded-lg border border-gray-200 px-3 text-left font-semibold text-gray-600 transition-colors hover:border-primary-300 hover:text-primary-700 sm:hidden"
                                >
                                    <span>{t('languageLabel')}</span>
                                    <span>{language === 'vi' ? 'English' : 'Tiếng Việt'}</span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => scrollToSection('contact')}
                                    className="mt-2 flex min-h-12 items-center justify-center gap-2 rounded-lg bg-primary-600 px-5 font-bold text-white hover:bg-primary-700"
                                >
                                    {t('startProject')} <FaArrowRight className="text-xs" />
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </header>

            <div className="h-[76px] sm:h-[84px]" aria-hidden="true" />
        </>
    );
};

export default Header;
